import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Divider,
  Fade,
  IconButton,
  Modal,
  Stack,
  Input,
  Typography,
} from '@mui/material';
import axios from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDebouncedCallback } from 'use-debounce';

import AudioPlayer from './audioPlayer';
import { AudioPlayerProvider, useAudioPlayer } from './audioPlayerContext';

import { useToastNotificationContext } from '../../../../../../hooks/global/alert/useToastNotification';
import {
  AWSMeetingTranscript,
  AWSMeetingTranscriptFirstLine,
  FilteredTranscript,
} from '../../../../../../interfaces/AWSMeetingTranscript';
import { MeetingTranscript } from '../../../../../../slices/reportSlice';

interface TranscriptRowProps {
  label: string;
  startTime: number;
  timestamp: number;
  content: string;
}

interface ViewTranscriptProps {
  open: boolean;
  onClose: () => void;
  meetingTranscript?: MeetingTranscript;
}

function TranscriptRow({
  label,
  startTime,
  timestamp,
  content,
}: TranscriptRowProps) {
  const { skipTo, secondsToTime } = useAudioPlayer();

  return (
    <Stack width='100%' gap={1}>
      <Stack direction='row' gap={2}>
        <Stack justifyContent='center' flexBasis={300}>
          {label}
        </Stack>
        <Stack justifyContent='center'>
          {dayjs(timestamp).format('hh:mm A')}
        </Stack>
        <Stack>
          <IconButton
            size='small'
            sx={{
              color: '#FFFFFF',
              background: (theme) => theme.palette.teal.main,
            }}
            onClick={() => {
              skipTo(startTime);
            }}
          >
            <PlayArrowIcon />
          </IconButton>
        </Stack>
        <Stack justifyContent='center'>{secondsToTime(startTime)}</Stack>
      </Stack>
      <Stack direction='row' paddingLeft={2} paddingRight={1}>
        <Typography
          sx={{
            color: 'var(--Neutral-White-BG-900, #3D3D3D)',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: '400',
            lineHeight: '150%',
          }}
        ></Typography>
        {content}
      </Stack>
    </Stack>
  );
}

function ViewTranscript({
  open,
  onClose,
  meetingTranscript,
}: ViewTranscriptProps) {
  const fileReader = new FileReader();
  const intl = useIntl();
  const { createToastNotification } = useToastNotificationContext();
  const [search, setSeach] = useState('');
  const [transcripts, setTranscripts] = useState<FilteredTranscript[]>([]);
  const [transcriptionStartedAt, setTranscriptionStartedAt] = useState<Dayjs>(
    dayjs(0),
  );
  const debouncedSearch = useDebouncedCallback((value: string) => {
    setSeach(value);
  }, 1000);

  const filteredTranscriptsArray = (rawTranscripts: string[] = []) =>
    rawTranscripts
      .map((rawTranscript) => {
        if (!rawTranscript) return null; // skip empty lines

        const parsedTranscript = JSON.parse(
          rawTranscript,
        ) as AWSMeetingTranscript;

        if (parsedTranscript.transcript) {
          return {
            ...parsedTranscript.transcript.results[0],
            content:
              parsedTranscript.transcript.results[0].alternatives[0].transcript,
            speakerExternalUserId:
              parsedTranscript.transcript.results[0].alternatives[0].items[0]
                .speakerExternalUserId,
          };
        }

        return null;
      })
      .filter(
        (transcriptResult) => transcriptResult && !transcriptResult.isPartial,
      ) as FilteredTranscript[];

  const handleFileLoad = () => {
    const result = fileReader.result;
    let rawTranscripts: string[] = [];
    if (typeof result === 'string') {
      rawTranscripts = result.split('\n');
      // remove first line, and parse to get start time of transcript.
      const rawFirstLine = rawTranscripts.shift();
      if (rawFirstLine) {
        const firstLine = JSON.parse(
          rawFirstLine,
        ) as AWSMeetingTranscriptFirstLine;
        setTranscriptionStartedAt(dayjs(firstLine.Status.eventTimeMs));
      }
    }

    const filteredTranscripts = filteredTranscriptsArray(rawTranscripts);
    setTranscripts(filteredTranscripts);
  };

  const downloadAndProcessTranscript = async (
    meetingTranscriptParams: MeetingTranscript,
  ) => {
    try {
      const downloadedTranscript = await axios
        .create({
          baseURL: meetingTranscriptParams.transcriptURL,
        })
        .get('', {
          responseType: 'blob',
          headers: {
            Accept: 'application/octet-stream',
          },
        });

      fileReader.addEventListener('load', handleFileLoad, false);
      fileReader.readAsText(downloadedTranscript.data);
    } catch (error) {
      createToastNotification(
        intl.formatMessage({ defaultMessage: 'Something went wrong' }),
        'error',
      );
    }
  };

  useEffect(() => {
    if (!meetingTranscript) return;

    downloadAndProcessTranscript(meetingTranscript);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingTranscript]);

  if (!meetingTranscript) {
    return null;
  }

  const { title, createdAt, audioSource } = meetingTranscript;
  const filteredTranscripts = transcripts.filter((transcript) => {
    if (!search) {
      return true;
    }

    return transcript.content.search(new RegExp(search, 'i')) >= 0;
  });

  return (
    <AudioPlayerProvider>
      <Modal open={open} onClose={onClose} closeAfterTransition>
        <Fade in={open}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '70vw',
              height: '80vh',
              padding: '20px',
              borderRadius: '12px',
              background: ' #FFF',
            }}
          >
            <Stack width='100%' height='100%'>
              <Stack direction='row' gap={2}>
                <Stack justifyContent='center'>{title}</Stack>
                <Stack
                  flexGrow={1}
                  justifyContent='center'
                  color='var(--neutral-white-bg-400-base, #878787)'
                >
                  <Stack direction='row' gap={1}>
                    <CalendarTodayIcon />
                    {dayjs(createdAt).format('MMM D, YYYY')}
                  </Stack>
                </Stack>
                <Stack justifyContent='center'>
                  <IconButton onClick={onClose}>
                    <CloseIcon />
                  </IconButton>
                </Stack>
              </Stack>
              <Stack
                direction={{
                  xs: 'column',
                  lg: 'row',
                }}
                gap={2}
              >
                <Stack flexGrow={1} justifyContent='center'>
                  <AudioPlayer src={audioSource} />
                </Stack>
                <Stack flexBasis='25%' padding={1}>
                  <Input
                    sx={{
                      border: '1px solid #D1D1D1',
                      borderRadius: '6px',
                      padding: '16px',
                      '.MuiInputBase-root': {
                        padding: '12px 16px',
                      },
                      '.MuiSvgIcon-root': {
                        marginRight: '8px',
                        color: '#878787',
                      },
                    }}
                    disableUnderline
                    startAdornment={<SearchIcon />}
                    onChange={(event) => {
                      debouncedSearch(event.currentTarget.value);
                    }}
                    fullWidth
                  />
                </Stack>
              </Stack>
              <Divider />
              <Stack gap={2.5} overflow='scroll' paddingTop={2}>
                {filteredTranscripts.map((transcript) => (
                  <TranscriptRow
                    key={transcript.resultId}
                    label={transcript.speakerExternalUserId}
                    startTime={dayjs(transcript.startTimeMs).diff(
                      transcriptionStartedAt,
                      'seconds',
                    )}
                    timestamp={transcript.startTimeMs}
                    content={transcript.content}
                  />
                ))}
                {isEmpty(filteredTranscripts) && (
                  <Stack direction='row' justifyContent='center' mt={2}>
                    No Transcription Found
                  </Stack>
                )}
              </Stack>
            </Stack>
          </Box>
        </Fade>
      </Modal>
    </AudioPlayerProvider>
  );
}

export default ViewTranscript;
